import React, { useState } from 'react';
import { Minus, Plus, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { clientPortalService } from '../../services/clientPortal.service';
import { toast } from 'sonner';

const MIN_REASON = 5;

/**
 * Change request dialog for an order that has already been invoiced in
 * advance. Quantities only — the bakery approves, then reconciles the invoice
 * with a credit or an additional invoice.
 */
export const RequestChangeDialog = ({ open, onOpenChange, order, onSubmitted }) => {
  const [quantities, setQuantities] = useState({});
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const items = order?.items || [];

  const qtyFor = (item) =>
    quantities[item.product_id] ?? item.quantity;

  const setQty = (item, next) => {
    setQuantities((prev) => ({ ...prev, [item.product_id]: Math.max(0, next) }));
  };

  const newTotal = items.reduce(
    (sum, item) => sum + (item.unit_price || 0) * qtyFor(item), 0,
  );
  const netDelta = newTotal - (order?.total_amount || 0);
  const hasChanges = items.some((item) => qtyFor(item) !== item.quantity);
  const keepsAnItem = items.some((item) => qtyFor(item) > 0);

  const reset = () => { setQuantities({}); setReason(''); };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await clientPortalService.createOrderChangeRequest(
        order.id,
        items.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: qtyFor(item),
        })),
        reason.trim(),
      );
      toast.success('Change request sent — the bakery will review it shortly');
      reset();
      onOpenChange(false);
      onSubmitted?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send the change request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!submitting) { onOpenChange(o); if (!o) reset(); } }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="request-change-dialog">
        <DialogHeader>
          <DialogTitle>Request a change — {order?.order_number}</DialogTitle>
          <DialogDescription>Adjust quantities and tell us why.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-800 leading-relaxed">
            Because this order is already invoiced, changes need bakery approval. If the total
            goes down you'll receive a credit; if it goes up you'll receive an additional invoice.
          </p>
        </div>

        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.product_id}
              className="flex items-center gap-3 p-3 rounded-lg bg-gray-50"
              data-testid={`change-item-${item.product_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{item.product_name}</p>
                <p className="text-xs text-gray-500">
                  ${(item.unit_price || 0).toFixed(2)} · ordered {item.quantity}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11"
                  onClick={() => setQty(item, qtyFor(item) - 1)}
                  data-testid={`change-item-minus-${item.product_id}`}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span
                  className="w-10 text-center font-semibold"
                  data-testid={`change-item-qty-${item.product_id}`}
                >
                  {qtyFor(item)}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11"
                  onClick={() => setQty(item, qtyFor(item) + 1)}
                  data-testid={`change-item-plus-${item.product_id}`}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div>
            <p className="text-xs text-gray-500 uppercase">Requested total</p>
            <p className="text-lg font-bold" data-testid="change-request-new-total">
              ${newTotal.toFixed(2)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase">Difference</p>
            <p
              className={`text-lg font-bold ${netDelta < 0 ? 'text-green-600' : netDelta > 0 ? 'text-amber-600' : 'text-gray-500'}`}
              data-testid="change-request-delta"
            >
              {netDelta < 0 ? '-' : netDelta > 0 ? '+' : ''}${Math.abs(netDelta).toFixed(2)}
            </p>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase text-gray-500">Reason (required)</Label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Our weekend event was scaled back, we need fewer glazed"
            className="mt-1"
            data-testid="change-request-reason"
          />
        </div>

        {!keepsAnItem && (
          <p className="text-sm text-red-600">
            An order must keep at least one item. Contact us to cancel it instead.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" className="min-h-[44px]" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            className="min-h-[44px] bg-blue-600 hover:bg-blue-700"
            onClick={handleSubmit}
            disabled={submitting || !hasChanges || !keepsAnItem || reason.trim().length < MIN_REASON}
            data-testid="submit-change-request-btn"
          >
            {submitting ? 'Sending…' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
